import { ref } from "vue";
import { defineStore } from "pinia";
import {
    PayerInformation,
    PartnerLinkInformation,
} from "@/helpers/interfaces/shared";

/**
 * Store for managing checkout-specific state, such as supplementary data, payer information, partner link information, and order ID. This store is designed
 * to be used across the checkout process to maintain consistency of data and
 * facilitate communication between different components and providers.
 * The store includes functions to set and update this information, as well as
 * flags to trigger validation of payer and card information when necessary.
 */
export const useCheckoutStore = defineStore(
    "checkout",
    () => {
        const supplementaryData = ref<any>(null);
        const payerInformation = ref<PayerInformation | null>(null);
        const triggerPayerInformationValidation = ref<boolean>(false);
        const triggerCardInformationValidation = ref<boolean>(false);
        const partnerLinkInformation = ref<PartnerLinkInformation | null>(null);
        const orderId = ref<string | null>(null);
        const willSendConfirmationEmail = ref(false);

        function setSupplementaryData(data: any) {
            supplementaryData.value = data;
        }

        function updateSupplementaryData(updatedData: any) {
            if (supplementaryData.value === null) {
                supplementaryData.value = {};
            }

            const updatedObject = { ...supplementaryData.value };

            for (const key in updatedData) {
                if (Object.prototype.hasOwnProperty.call(updatedData, key)) {
                    const updatedValue = (updatedData as any)[key];
                    (updatedObject as any)[key] = updatedValue;
                }
            }

            supplementaryData.value = updatedObject;
        }

        function setPayerDetails(information: PayerInformation | null) {
            payerInformation.value = information;
        }

        function setTriggerPayerInformationValidation(value: boolean) {
            triggerPayerInformationValidation.value = value;
        }

        function setTriggerCardInformationValidation(value: boolean) {
            triggerCardInformationValidation.value = value;
        }

        function setPartnerLinkInformation(
            information: PartnerLinkInformation | null,
        ) {
            partnerLinkInformation.value = information;
        }

        function updatePartnerLinkInformation(updatedData: any) {
            if (partnerLinkInformation.value === null) {
                partnerLinkInformation.value = {};
            }

            const updatedObject = { ...partnerLinkInformation.value };

            for (const key in updatedData) {
                if (Object.prototype.hasOwnProperty.call(updatedData, key)) {
                    const updatedValue = (updatedData as any)[key];
                    (updatedObject as any)[key] = updatedValue;
                }
            }

            partnerLinkInformation.value = updatedObject;
        }

        function setOrderId(id: string | null) {
            orderId.value = id;
        }

        function setWillSendConfirmationEmail(willSend: boolean) {
            willSendConfirmationEmail.value = willSend;
        }

        return {
            supplementaryData,
            payerInformation,
            triggerPayerInformationValidation,
            triggerCardInformationValidation,
            partnerLinkInformation,
            orderId,
            willSendConfirmationEmail,
            setSupplementaryData,
            updateSupplementaryData,
            setPayerDetails,
            setTriggerPayerInformationValidation,
            setTriggerCardInformationValidation,
            setPartnerLinkInformation,
            updatePartnerLinkInformation,
            setOrderId,
            setWillSendConfirmationEmail,
        };
    },
    {
        persist: {
            pick: [
                "supplementaryData",
                "payerInformation",
                "triggerPayerInformationValidation",
                "triggerCardInformationValidation",
                "partnerLinkInformation",
                "orderId",
            ],
            storage: sessionStorage,
        },
    },
);
